const STORAGE_KEY = 'pfClaimsPricingConfig_v2';
const LEGACY_STORAGE_KEY = 'pfClaimsPricingConfig';

/**
 * Unified Excel-aligned case factors.
 * type: 'flag' = Yes/No (0/1), 'count' = numeric quantity
 * waitMode: 'once' = external wait applied once if factor > 0
 *           'per'  = external wait * count (rarely used; default once)
 */
/** All factors use numeric values (0 = off / not in scope).
 *  `requirement` is the trackable name — kept identical to `label`. */
export const CASE_FACTORS = [
  { key: 'activation', label: 'Activation', requirement: 'Activation', type: 'count', scoreWeight: 2, ourDays: 1, waitDays: 7, waitMode: 'once' },
  { key: 'aadhar_linking', label: 'Aadhar Linking', requirement: 'Aadhar Linking', type: 'count', scoreWeight: 25, ourDays: 1, waitDays: 10, waitMode: 'once' },
  { key: 'jd_issues', label: 'JD Issues (No.)', requirement: 'JD Issues (No.)', type: 'count', scoreWeight: 3, ourDays: 2, waitDays: 15, waitMode: 'once' },
  { key: 'kyc_issues', label: 'KYC Issues (No.)', requirement: 'KYC Issues (No.)', type: 'count', scoreWeight: 2, ourDays: 1, waitDays: 10, waitMode: 'once' },
  { key: 'e_nom', label: 'e-Nomination', requirement: 'e-Nomination', type: 'count', scoreWeight: 2, ourDays: 1, waitDays: 5, waitMode: 'once' },
  { key: 'exit_date', label: 'Exit Date (No.)', requirement: 'Exit Date (No.)', type: 'count', scoreWeight: 2, ourDays: 1, waitDays: 15, waitMode: 'once' },
  { key: 'transfers', label: 'Transfers (No.)', requirement: 'Transfers (No.)', type: 'count', scoreWeight: 2, ourDays: 1, waitDays: 22, waitMode: 'once' },
  { key: 'advance_claims', label: 'Advance Claims (Form-31)', requirement: 'Advance Claims (Form-31)', type: 'count', scoreWeight: 2, ourDays: 1, waitDays: 20, waitMode: 'once' },
  { key: 'pf_final_settlement', label: 'PF Final Settlement (Form-19)', requirement: 'PF Final Settlement (Form-19)', type: 'count', scoreWeight: 2, ourDays: 2, waitDays: 25, waitMode: 'once' },
  { key: 'pension_claim', label: 'Pension Claim (Form-10D)', requirement: 'Pension Claim (Form-10D)', type: 'count', scoreWeight: 25, ourDays: 2, waitDays: 30, waitMode: 'once' },
  { key: 'scheme_certificate', label: 'Scheme Certificate (Form-10C)', requirement: 'Scheme Certificate (Form-10C)', type: 'count', scoreWeight: 15, ourDays: 2, waitDays: 25, waitMode: 'once' },
  { key: 'manual_pension', label: 'Monthly Pension Application', requirement: 'Monthly Pension Application', type: 'count', scoreWeight: 20, ourDays: 3, waitDays: 30, waitMode: 'once' },
  { key: 'death_claim', label: 'EDLI Claim', requirement: 'EDLI Claim', type: 'count', scoreWeight: 5, ourDays: 3, waitDays: 30, waitMode: 'once' },
  { key: 'eps_merging', label: 'EPS Merging Issues (No.)', requirement: 'EPS Merging Issues (No.)', type: 'count', scoreWeight: 5, ourDays: 2, waitDays: 20, waitMode: 'once' },
  { key: 'dilink', label: 'Dilink (No.)', requirement: 'Dilink (No.)', type: 'count', scoreWeight: 2, ourDays: 1, waitDays: 15, waitMode: 'once' },
  { key: 'objections', label: 'Objections Required (No.)', requirement: 'Objections Required (No.)', type: 'count', scoreWeight: 5, ourDays: 2, waitDays: 20, waitMode: 'once' },
  { key: 'physical_visit', label: 'Physical Visit Required (No.)', requirement: 'Physical Visit Required (No.)', type: 'count', scoreWeight: 10, ourDays: 1, waitDays: 0, waitMode: 'once' },
  { key: 'contribution_adjustment', label: 'Contribution Adjustment', requirement: 'Contribution Adjustment', type: 'count', scoreWeight: 0, ourDays: 1, waitDays: 10, waitMode: 'once' }
];

export const STATUS_OPTIONS = ['Not Started', 'Work in Progress', 'Pending with Employer/PF Office', 'Completed'];

/** Status DB/API field keyed by case-factor key */
export const FACTOR_STATUS_FIELD = {
  activation: 'pf_activation_status',
  aadhar_linking: 'aadhar_linking_status',
  jd_issues: 'joint_declaration_filing_status',
  kyc_issues: 'kyc_status',
  e_nom: 'e_nomination_status',
  exit_date: 'exit_date_status',
  transfers: 'transfers_status',
  advance_claims: 'advance_claim_status',
  pf_final_settlement: 'pf_final_settlement_status',
  pension_claim: 'monthly_pension_application_status',
  scheme_certificate: 'scheme_certificate_status',
  manual_pension: 'manual_pension_apply_status',
  death_claim: 'death_claim_pf_edli_eps_application_status',
  eps_merging: 'eps_merging_status',
  dilink: 'dilink_status',
  objections: 'objections_status',
  physical_visit: 'physical_visit_status',
  contribution_adjustment: 'contribution_adjustment_status'
};

/** Status field keyed by display/requirement name (current labels + legacy aliases) */
export const REQUIREMENT_STATUS_FIELD = {
  ...CASE_FACTORS.reduce((acc, f) => {
    const field = FACTOR_STATUS_FIELD[f.key];
    if (field) acc[f.label] = field;
    return acc;
  }, {}),
  // Legacy requirement names (older claims)
  'PF Activation': 'pf_activation_status',
  'Joint Declaration Filing': 'joint_declaration_filing_status',
  'KYC Updation': 'kyc_status',
  'E-Nomination Filing': 'e_nomination_status',
  'Exit Date Correction': 'exit_date_status',
  Transfers: 'transfers_status',
  'PF Advance Claim': 'advance_claim_status',
  'PF Final Settlement': 'pf_final_settlement_status',
  'Scheme Certificate Application': 'scheme_certificate_status',
  'Monthly Pension application': 'monthly_pension_application_status',
  'Manual Pension Apply': 'manual_pension_apply_status',
  'Monthly Pension Application': 'manual_pension_apply_status',
  'Death Claim - PF, EDLI & EPS application': 'death_claim_pf_edli_eps_application_status',
  'Death Claim Handling': 'death_claim_pf_edli_eps_application_status',
  'EDLI Claim': 'death_claim_pf_edli_eps_application_status',
  'EPS Merging Issues': 'eps_merging_status'
};

/** requirement/label → case factor key */
export const REQUIREMENT_TO_FACTOR_KEY = CASE_FACTORS.reduce((acc, f) => {
  if (f.requirement) acc[f.requirement] = f.key;
  if (f.label) acc[f.label] = f.key;
  return acc;
}, {
  'PF Activation': 'activation',
  'Joint Declaration Filing': 'jd_issues',
  'KYC Updation': 'kyc_issues',
  'E-Nomination Filing': 'e_nom',
  'Exit Date Correction': 'exit_date',
  Transfers: 'transfers',
  'PF Advance Claim': 'advance_claims',
  'PF Final Settlement': 'pf_final_settlement',
  'Scheme Certificate Application': 'scheme_certificate',
  'Monthly Pension application': 'pension_claim',
  'Manual Pension Apply': 'manual_pension',
  'Monthly Pension Application': 'manual_pension',
  'Death Claim - PF, EDLI & EPS application': 'death_claim',
  'Death Claim Handling': 'death_claim',
  'EDLI Claim': 'death_claim',
  'EPS Merging Issues': 'eps_merging'
});

/** Map legacy requirement labels to current Case Factor labels */
export const normalizeRequirementLabel = (name) => {
  const aliases = {
    'Death Claim Handling': 'EDLI Claim',
    'Death Claim - PF, EDLI & EPS application': 'EDLI Claim',
    'Death Claim - PF, EDLI & EPS': 'EDLI Claim',
    'Manual Pension Apply': 'Monthly Pension Application',
    'PF Activation': 'Activation',
    'Joint Declaration Filing': 'JD Issues (No.)',
    'KYC Updation': 'KYC Issues (No.)',
    'E-Nomination Filing': 'e-Nomination',
    'Exit Date Correction': 'Exit Date (No.)',
    Transfers: 'Transfers (No.)',
    'PF Advance Claim': 'Advance Claims (Form-31)',
    'PF Final Settlement': 'PF Final Settlement (Form-19)',
    'Scheme Certificate Application': 'Scheme Certificate (Form-10C)',
    'Monthly Pension application': 'Pension Claim (Form-10D)',
    'EPS Merging Issues': 'EPS Merging Issues (No.)'
  };
  return aliases[name] || name;
};

export const normalizeRequirementList = (list = []) =>
  [...new Set((list || []).map((n) => normalizeRequirementLabel(String(n || '').trim())).filter(Boolean))];

/** Resolve factor counts from claim pricing (source of truth for status tracking) */
export const resolveClaimFactorValues = (claim = {}) => {
  const pricing = typeof claim.pricing_data === 'string'
    ? (() => { try { return JSON.parse(claim.pricing_data); } catch { return {}; } })()
    : (claim.pricing_data || {});
  const values = { ...emptyFactorValues(), ...(pricing.factorValues || {}) };
  const hasAny = CASE_FACTORS.some((f) => (parseFloat(values[f.key]) || 0) > 0);
  if (hasAny) return values;

  // Fallback: map saved requirement labels onto counts of 1
  const saved = Array.isArray(claim.requirements)
    ? claim.requirements
    : (claim.requirement ? String(claim.requirement).split(',').map((r) => r.trim()).filter(Boolean) : []);
  saved.forEach((name) => {
    const key = REQUIREMENT_TO_FACTOR_KEY[name] || REQUIREMENT_TO_FACTOR_KEY[normalizeRequirementLabel(name)];
    if (key && (parseFloat(values[key]) || 0) <= 0) values[key] = 1;
  });
  return values;
};

/** Case factors with count > 0 (source of truth for Update Claim Status) */
export const getActiveCaseFactors = (factorValues = {}) =>
  CASE_FACTORS.filter(
    (f) => (parseFloat(factorValues[f.key]) || 0) > 0 && FACTOR_STATUS_FIELD[f.key]
  );

export const DEFAULT_FEE_BRACKETS = [
  { minScore: 301, type: 'percent', value: 10, label: 'Score > 300 → 10% of PF balance' },
  { minScore: 251, type: 'fixed', value: 100000, label: 'Score > 250 → ₹1,00,000' },
  { minScore: 201, type: 'fixed', value: 90000, label: 'Score > 200 → ₹90,000' },
  { minScore: 176, type: 'fixed', value: 80000, label: 'Score > 175 → ₹80,000' },
  { minScore: 151, type: 'fixed', value: 70000, label: 'Score > 150 → ₹70,000' },
  { minScore: 126, type: 'fixed', value: 60000, label: 'Score > 125 → ₹60,000' },
  { minScore: 101, type: 'fixed', value: 50000, label: 'Score > 100 → ₹50,000' },
  { minScore: 81, type: 'fixed', value: 35000, label: 'Score > 80 → ₹35,000' },
  { minScore: 61, type: 'fixed', value: 30000, label: 'Score > 60 → ₹30,000' },
  { minScore: 41, type: 'fixed', value: 25000, label: 'Score > 40 → ₹25,000' },
  { minScore: 31, type: 'fixed', value: 10000, label: 'Score > 30 → ₹10,000' },
  { minScore: 6, type: 'fixed', value: 5000, label: 'Score > 5 → ₹5,000' },
  { minScore: 0, type: 'fixed', value: 3000, label: 'Else → ₹3,000' }
];

export const DEFAULT_QUOTE_TERMS = [
  'Timeline is estimated in working days and may vary based on employer/EPFO response.',
  '30% advance payment is preferred; balance is payable as activities are completed.',
  'Documentation, follow-up and process handling will be managed by Paytiemp. Customer support is required for physical signatures/employer correspondence when needed.',
  'Progress updates will be shared with the customer regularly.',
  'Documents required: Aadhaar (member & family), PAN, bank details, and passport-size photos (member & family).',
  'Scope of service is limited to the activities listed in this quote.',
  'Physical visit charges to the respective PF office, if required, will be additional and agreed mutually before proceeding.'
];

const defaultFactorSettings = () => {
  const scoreWeights = {};
  const ourDays = {};
  const waitDays = {};
  CASE_FACTORS.forEach((f) => {
    scoreWeights[f.key] = f.scoreWeight;
    ourDays[f.key] = f.ourDays;
    waitDays[f.key] = f.waitDays;
  });
  return { scoreWeights, ourDays, waitDays };
};

export const createDefaultPricingConfig = (effectiveFrom = new Date().toISOString().slice(0, 10)) => {
  const { scoreWeights, ourDays, waitDays } = defaultFactorSettings();
  return {
    id: `cfg_${Date.now()}`,
    name: 'Default Setup',
    effectiveFrom,
    markupPercent: 120,
    advancePercentDefault: 30,
    workingDaysPerMonth: 22,
    timelineBufferDays: 5,
    scoreWeights,
    ourDays,
    waitDays,
    feeBrackets: DEFAULT_FEE_BRACKETS,
    quoteTerms: DEFAULT_QUOTE_TERMS,
    updatedAt: new Date().toISOString()
  };
};

const migrateLegacyConfig = (cfg) => {
  const base = createDefaultPricingConfig(cfg.effectiveFrom || '2000-01-01');
  return {
    ...base,
    ...cfg,
    scoreWeights: { ...base.scoreWeights, ...(cfg.scoreWeights || {}) },
    ourDays: { ...base.ourDays, ...(cfg.ourDays || {}) },
    waitDays: { ...base.waitDays, ...(cfg.waitDays || {}) },
    workingDaysPerMonth: cfg.workingDaysPerMonth || 22,
    timelineBufferDays: cfg.timelineBufferDays ?? 5,
    feeBrackets: Array.isArray(cfg.feeBrackets) && cfg.feeBrackets.length ? cfg.feeBrackets : DEFAULT_FEE_BRACKETS,
    quoteTerms: Array.isArray(cfg.quoteTerms) && cfg.quoteTerms.length ? cfg.quoteTerms : DEFAULT_QUOTE_TERMS
  };
};

const normalizeConfigs = (configs) => {
  if (!Array.isArray(configs) || configs.length === 0) {
    return [createDefaultPricingConfig('2000-01-01')];
  }
  return configs
    .map(migrateLegacyConfig)
    .sort((a, b) => String(b.effectiveFrom).localeCompare(String(a.effectiveFrom)));
};

export const getPricingConfigs = () => {
  try {
    let raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
      if (legacy) {
        const migrated = normalizeConfigs(JSON.parse(legacy));
        localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
        return migrated;
      }
      const defaults = [createDefaultPricingConfig('2000-01-01')];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(defaults));
      return defaults;
    }
    return normalizeConfigs(JSON.parse(raw));
  } catch (error) {
    console.warn('Failed to load setup configs:', error);
    return [createDefaultPricingConfig('2000-01-01')];
  }
};

export const savePricingConfigs = (configs) => {
  const normalized = normalizeConfigs(configs);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  return normalized;
};

export const getActivePricingConfig = (asOfDate = new Date()) => {
  const configs = getPricingConfigs();
  const dateStr = typeof asOfDate === 'string'
    ? asOfDate.slice(0, 10)
    : asOfDate.toISOString().slice(0, 10);
  return configs.find((cfg) => String(cfg.effectiveFrom).slice(0, 10) <= dateStr) || configs[configs.length - 1];
};

export const emptyFactorValues = () => {
  const values = {};
  CASE_FACTORS.forEach((f) => {
    values[f.key] = 0;
  });
  return values;
};

export const factorsToRequirements = (factorValues = {}) =>
  getActiveCaseFactors(factorValues).map((f) => f.label);

export const requirementsToFactorDefaults = (requirements = [], existing = {}) => {
  const values = { ...emptyFactorValues(), ...existing };
  CASE_FACTORS.forEach((f) => {
    const names = [f.requirement, f.label].filter(Boolean);
    if (names.some((n) => requirements.includes(n))) {
      if ((parseFloat(values[f.key]) || 0) <= 0) {
        values[f.key] = 1;
      }
    }
  });
  return values;
};

export const calculateTotalScore = (factorValues, config) => {
  const weights = config.scoreWeights || {};
  return CASE_FACTORS.reduce((sum, f) => {
    const qty = parseFloat(factorValues[f.key]) || 0;
    return sum + qty * (parseFloat(weights[f.key]) || 0);
  }, 0);
};

export const calculateSuggestedFee = (score, pfBalance, config) => {
  const brackets = [...(config.feeBrackets || DEFAULT_FEE_BRACKETS)].sort((a, b) => b.minScore - a.minScore);
  for (const bracket of brackets) {
    if (score >= bracket.minScore) {
      if (bracket.type === 'percent') {
        return Math.round(((parseFloat(pfBalance) || 0) * (parseFloat(bracket.value) || 0)) / 100);
      }
      return Math.round(parseFloat(bracket.value) || 0);
    }
  }
  return 3000;
};

export const formatWorkingTimeline = (totalWorkingDays, workingDaysPerMonth = 22) => {
  const total = Math.max(0, Math.round(totalWorkingDays || 0));
  const perMonth = Math.max(1, parseFloat(workingDaysPerMonth) || 22);
  const months = Math.floor(total / perMonth);
  const days = total % perMonth;
  const short = `${months}M.${days}D`;
  const long = `${months} Month(s), ${days} Day(s)`;
  return { totalWorkingDays: total, months, days, short, long, label: long };
};

/**
 * Timeline = sum(ourDays * qty) + max(external wait among active factors) + buffer
 */
export const calculateTimelineWorkingDays = (factorValues, config) => {
  const ourDaysMap = config.ourDays || {};
  const waitDaysMap = config.waitDays || {};
  const buffer = parseFloat(config.timelineBufferDays) ?? 5;

  let ourTotal = 0;
  let maxWait = 0;
  const breakdown = [];

  CASE_FACTORS.forEach((f) => {
    const qty = parseFloat(factorValues[f.key]) || 0;
    if (qty <= 0) return;
    const ourUnit = parseFloat(ourDaysMap[f.key]) || 0;
    const waitUnit = parseFloat(waitDaysMap[f.key]) || 0;
    const ourPart = ourUnit * qty;
    const waitPart = f.waitMode === 'per' ? waitUnit * qty : waitUnit;
    ourTotal += ourPart;
    if (waitPart > maxWait) maxWait = waitPart;
    breakdown.push({
      key: f.key,
      label: f.label,
      qty,
      ourDays: ourPart,
      waitDays: waitPart
    });
  });

  const hasWork = ourTotal > 0 || maxWait > 0;
  const total = hasWork ? ourTotal + maxWait + buffer : 0;
  const formatted = formatWorkingTimeline(total, config.workingDaysPerMonth);

  return {
    ...formatted,
    ourDaysTotal: ourTotal,
    maxExternalWait: maxWait,
    bufferDays: hasWork ? buffer : 0,
    breakdown
  };
};

export const calculatePricing = ({
  factorValues = {},
  pfBalance = 0,
  finalAgreedFee = null,
  initialAdvance = null,
  asOfDate = new Date(),
  configOverride = null
}) => {
  const config = configOverride || getActivePricingConfig(asOfDate);
  const values = { ...emptyFactorValues(), ...factorValues };
  const totalScore = calculateTotalScore(values, config);
  const suggestedFee = calculateSuggestedFee(totalScore, pfBalance, config);
  const markupPercent = parseFloat(config.markupPercent) || 120;
  const listFee = Math.round(suggestedFee * (markupPercent / 100));
  const agreed = finalAgreedFee === null || finalAgreedFee === ''
    ? null
    : Math.round(parseFloat(finalAgreedFee) || 0);
  const discount = agreed === null ? 0 : Math.max(0, listFee - agreed);
  const discountPercent = agreed === null || listFee <= 0 ? 0 : Math.round((discount / listFee) * 10000) / 100;
  const timeline = calculateTimelineWorkingDays(values, config);
  const advancePercent = parseFloat(config.advancePercentDefault) || 30;
  const defaultAdvance = agreed === null ? 0 : Math.round((agreed * advancePercent) / 100);
  const advanceValue = initialAdvance === null || initialAdvance === ''
    ? defaultAdvance
    : Math.round(parseFloat(initialAdvance) || 0);

  const selectedActivities = CASE_FACTORS
    .filter((f) => f.requirement && (parseFloat(values[f.key]) || 0) > 0)
    .map((f) => ({
      name: f.requirement,
      label: f.label,
      value: parseFloat(values[f.key]) || 0
    }));

  const complexityLines = CASE_FACTORS
    .filter((f) => (parseFloat(values[f.key]) || 0) > 0)
    .map((f) => ({
      name: f.label,
      value: parseFloat(values[f.key]) || 0
    }));

  return {
    configId: config.id,
    effectiveFrom: config.effectiveFrom,
    factorValues: values,
    requirements: factorsToRequirements(values),
    totalScore,
    suggestedFee,
    listFee,
    markupPercent,
    finalAgreedFee: agreed,
    discount,
    discountPercent,
    timeline,
    timelineMonths: timeline.months,
    timelineLabel: timeline.label,
    timelineShort: timeline.short,
    totalWorkingDays: timeline.totalWorkingDays,
    initialAdvance: advanceValue,
    defaultAdvance,
    advancePercent,
    selectedActivities,
    complexityLines,
    quoteTerms: config.quoteTerms || DEFAULT_QUOTE_TERMS,
    workingDaysPerMonth: config.workingDaysPerMonth || 22
  };
};

export const calculateFinalStatus = (requirements = [], statuses = {}) => {
  if (!requirements.length) return 'Not Started';
  const values = requirements.map((req) => {
    if (typeof req === 'object' && req?.key) {
      const field = FACTOR_STATUS_FIELD[req.key];
      return (field && statuses[field]) || 'Not Started';
    }
    return statuses[REQUIREMENT_STATUS_FIELD[req]] || 'Not Started';
  });
  if (values.every((s) => s === 'Not Started')) return 'Not Started';
  if (values.every((s) => s === 'Completed')) return 'Completed';
  return 'Work in Progress';
};

export default {
  CASE_FACTORS,
  getPricingConfigs,
  savePricingConfigs,
  getActivePricingConfig,
  createDefaultPricingConfig,
  calculatePricing,
  emptyFactorValues,
  factorsToRequirements,
  getActiveCaseFactors,
  resolveClaimFactorValues,
  normalizeRequirementLabel,
  normalizeRequirementList,
  formatWorkingTimeline,
  calculateFinalStatus,
  REQUIREMENT_STATUS_FIELD,
  FACTOR_STATUS_FIELD,
  REQUIREMENT_TO_FACTOR_KEY,
  STATUS_OPTIONS
};
